import React, { useEffect, useState } from "react";
import {
	Box,
	Typography,
	Button,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	TextField,
	Select,
	MenuItem,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
	FormControlLabel,
	Switch,
} from "@mui/material";
import supabase from "../../../supabaseClient";
import {
	cstDateAndTimeToIso,
	formatHoursLine,
	isoToCstDateAndTime,
} from "../../../utils/cstDateTime";

export default function AdminManageHours() {
	const [hours, setHours] = useState([]);
	const [loading, setLoading] = useState(true);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editHour, setEditHour] = useState(null);
	const [useDefaultHours, setUseDefaultHours] = useState(true);
	const [settingsSaving, setSettingsSaving] = useState(false);
	const [form, setForm] = useState({
		hour_type: "farm",
		date: "",
		open_time: "",
		close_time: "",
	});

	const fetchHoursData = async () => {
		setLoading(true);
		const [{ data: settingsData }, { data: hoursData }] = await Promise.all([
			supabase
				.from("business_hours_settings")
				.select("use_default_hours")
				.eq("singleton", true)
				.maybeSingle(),
			supabase
				.from("business_hours")
				.select("*")
				.order("opens_at", { ascending: true }),
		]);

		setUseDefaultHours(settingsData?.use_default_hours ?? true);
		setHours(hoursData || []);
		setLoading(false);
	};

	useEffect(() => {
		fetchHoursData();
	}, []);

	const handleOpen = (hour = null) => {
		setEditHour(hour);
		if (!hour) {
			setForm({
				hour_type: "farm",
				date: "",
				open_time: "",
				close_time: "",
			});
			setDialogOpen(true);
			return;
		}

		const openValues = isoToCstDateAndTime(hour.opens_at);
		const closeValues = isoToCstDateAndTime(hour.closes_at);
		setForm({
			hour_type: hour.hour_type || "farm",
			date: openValues.date,
			open_time: openValues.time,
			close_time: closeValues.time,
		});
		setDialogOpen(true);
	};

	const handleClose = () => {
		setDialogOpen(false);
		setEditHour(null);
	};

	const handleChange = (e) => {
		setForm({ ...form, [e.target.name]: e.target.value });
	};

	const handleSave = async () => {
		const opens_at = cstDateAndTimeToIso(form.date, form.open_time);
		const closes_at = cstDateAndTimeToIso(form.date, form.close_time);

		if (!opens_at || !closes_at) {
			alert("Please choose a date, opening time, and closing time.");
			return;
		}

		if (new Date(closes_at) <= new Date(opens_at)) {
			alert("Closing time must be after opening time.");
			return;
		}

		setLoading(true);
		const payload = {
			hour_type: form.hour_type,
			opens_at,
			closes_at,
		};

		const result = editHour
			? await supabase.from("business_hours").update(payload).eq("id", editHour.id)
			: await supabase.from("business_hours").insert([payload]);

		if (result.error) {
			alert("Error saving hours: " + result.error.message);
		} else {
			await fetchHoursData();
			handleClose();
		}
		setLoading(false);
	};

	const handleDelete = async (id) => {
		setLoading(true);
		const result = await supabase.from("business_hours").delete().eq("id", id);
		if (result.error) {
			alert("Error deleting hours: " + result.error.message);
		} else {
			await fetchHoursData();
		}
		setLoading(false);
	};

	const handleDefaultHoursToggle = async (_, checked) => {
		setUseDefaultHours(checked);
		setSettingsSaving(true);
		const result = await supabase.from("business_hours_settings").upsert(
			{
				singleton: true,
				use_default_hours: checked,
			},
			{ onConflict: "singleton" },
		);
		if (result.error) {
			setUseDefaultHours(!checked);
			alert("Error updating default hours setting: " + result.error.message);
		}
		setSettingsSaving(false);
	};

	return (
		<Box sx={{ mx: "auto", width: "100%" }}>
			<Typography variant="h5" gutterBottom>
				Manage Hours
			</Typography>
			<FormControlLabel
				control={
					<Switch
						checked={useDefaultHours}
						onChange={handleDefaultHoursToggle}
						disabled={settingsSaving}
					/>
				}
				label="Show default hours on homepage"
			/>
			<Box sx={{ mb: 2 }}>
				<Button
					variant="contained"
					color="primary"
					onClick={() => handleOpen()}
					sx={{ mt: 1 }}
				>
					Add Day Hours
				</Button>
			</Box>
			{loading ? (
				<CircularProgress />
			) : (
				<Box sx={{ width: "100%", overflowX: "auto" }}>
					<Table size="small" sx={{ minWidth: 500, width: "100%" }}>
						<TableHead>
							<TableRow>
								<TableCell sx={{ minWidth: 100 }}>Type</TableCell>
								<TableCell sx={{ minWidth: 220 }}>Hours (CST/CDT)</TableCell>
								<TableCell sx={{ minWidth: 80 }}>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{hours.map((hour) => (
								<TableRow key={hour.id}>
									<TableCell>
										{hour.hour_type === "farmstand"
											? "Farmstand"
											: "Farm & Greenhouse"}
									</TableCell>
									<TableCell>{formatHoursLine(hour.opens_at, hour.closes_at)}</TableCell>
									<TableCell>
										<Button size="small" onClick={() => handleOpen(hour)}>
											Edit
										</Button>
										<Button
											size="small"
											color="error"
											onClick={() => handleDelete(hour.id)}
										>
											Delete
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			)}
			<Dialog
				open={dialogOpen}
				onClose={handleClose}
				fullWidth
				maxWidth="sm"
				sx={{
					"& .MuiDialog-paper": {
						width: "100%",
						maxWidth: 400,
						m: 1,
					},
				}}
			>
				<DialogTitle>{editHour ? "Edit Day Hours" : "Add Day Hours"}</DialogTitle>
				<DialogContent sx={{ p: 1 }}>
					<Box mt={2}>
						<Typography variant="subtitle2" gutterBottom>
							Hour Type
						</Typography>
						<Select
							name="hour_type"
							value={form.hour_type}
							onChange={handleChange}
							fullWidth
							size="small"
						>
							<MenuItem value="farm">Farm & Greenhouse</MenuItem>
							<MenuItem value="farmstand">Farmstand</MenuItem>
						</Select>
					</Box>
					<TextField
						label="Date"
						name="date"
						value={form.date}
						onChange={handleChange}
						type="date"
						fullWidth
						margin="normal"
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
					<TextField
						label="Open Time (CST/CDT)"
						name="open_time"
						value={form.open_time}
						onChange={handleChange}
						type="time"
						fullWidth
						margin="normal"
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
					<TextField
						label="Close Time (CST/CDT)"
						name="close_time"
						value={form.close_time}
						onChange={handleChange}
						type="time"
						fullWidth
						margin="normal"
						size="small"
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} disabled={loading}>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						variant="contained"
						color="primary"
						disabled={loading}
					>
						{loading ? <CircularProgress size={20} /> : "Save"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

